<!-- eslint-disable no-console -->
<template>
  <main class="mx-auto">
    <CommonBanner
      :data="banner"
      :style="{
        'background-image': `url(${bgImage})`
      }"
      :flatter="true"
    />

    <section
      id="form-contact"
      class="container mx-auto flex flex-col md:flex-row my-6 md:my-15 w-[90%] md:w-full md:gap-x-20 lg:gap-x-52"
    >
      <div ref="formWrapper" class="w-full md:w-1/2 text-bw-900">
        <h2 class="section-heading">Let's Talk About Business</h2>
        <Transition name="contact-form">
          <form
            v-if="!isSubmitted"
            ref="contactForm"
            class="mt-10 w-full form"
            @submit.prevent="submitForm"
          >
            <div class="mb-6">
              <label class="block mr-4" for="subject"
                >Subject<span class="text-[#EB3133]">*</span></label
              >
              <v-select
                id="subject"
                v-model="formData.subject"
                label="name"
                placeholder="Projects"
                :options="options.subject"
                :reduce="option => option.subject"
                class="w-full md:w-[90%] text-bw-400"
              >
              </v-select>
              <div
                v-for="error in v$.subject.$errors"
                :key="error.$uid"
                class="mt-2"
              >
                <div class="text-red-600 flex items-center gap-2 text-sm">
                  <NuxtIcon name="cross" /> {{ error.$message }}
                </div>
              </div>
            </div>
            <div class="mb-6">
              <label class="block mr-4" for="name"
                >Name<span class="text-[#EB3133]">*</span></label
              >
              <input
                id="name"
                v-model.trim="formData.name"
                type="text"
                required
                class="form-input border-grey-400 border px-4 py-2"
                :class="{ 'error-input': v$.name.$error }"
                autocomplete="off"
                placeholder="Type your name"
                @blur="v$.name.$touch()"
              />
              <div
                v-for="error in v$.name.$errors"
                :key="error.$uid"
                class="mt-2"
              >
                <div class="text-red-600 flex items-center gap-2 text-sm">
                  <NuxtIcon name="cross" /> {{ error.$message }}
                </div>
              </div>
            </div>
            <div class="mb-6">
              <label class="block mr-4" for="company"
                >Company/Institution<span class="text-[#EB3133]">*</span></label
              >
              <input
                id="company"
                v-model.trim="formData.company"
                type="text"
                required
                class="form-input border-grey-400 border px-4 py-2"
                :class="{ 'error-input': v$.company.$error }"
                autocomplete="off"
                placeholder="Type your company"
                @blur="v$.company.$touch()"
              />
              <div
                v-for="error in v$.company.$errors"
                :key="error.$uid"
                class="mt-2"
              >
                <div class="text-red-600 flex items-center gap-2 text-sm">
                  <NuxtIcon name="cross" /> {{ error.$message }}
                </div>
              </div>
            </div>
            <div class="mb-6">
              <label class="block mr-4" for="email"
                >Work Email<span class="text-[#EB3133]">*</span></label
              >
              <input
                id="email"
                v-model.trim="formData.email"
                type="email"
                required
                class="form-input border-grey-400 border px-4 py-2"
                :class="{ 'error-input': v$.email.$error }"
                placeholder="Type your e-mail"
                @blur="v$.email.$touch()"
              />
              <div
                v-for="error in v$.email.$errors"
                :key="error.$uid"
                class="mt-2"
              >
                <div class="text-red-600 flex items-center gap-2 text-sm">
                  <NuxtIcon name="cross" /> {{ error.$message }}
                </div>
              </div>
            </div>
            <div class="mb-6">
              <label class="block mr-4" for="email"
                >Mobile Phone Number<span class="text-[#EB3133]">*</span></label
              >
              <input
                id="phone"
                v-model="formData.phone"
                type="tel"
                required
                class="form-input number-input border-grey-400 border px-4 py-2"
                :maxlength="v$.phone.maxLength.max"
                :class="{ 'error-input': v$.phone.$error }"
                placeholder="Type your phone number"
                @blur="v$.phone.$touch()"
              />
              <div
                v-for="error in v$.phone.$errors"
                :key="error.$uid"
                class="mt-2"
              >
                <div class="text-red-600 flex items-center gap-2 text-sm">
                  <NuxtIcon name="cross" />
                  {{
                    error.$validator === 'numPlusValidator'
                      ? 'Must be a number or use "+" if using country code'
                      : error.$message
                  }}
                </div>
              </div>
            </div>
            <div class="mb-6">
              <label class="block mr-4" for="country"
                >Country<span class="text-[#EB3133]">*</span></label
              >
              <v-select
                id="country"
                v-model="formData.country"
                label="name"
                placeholder="Select your country"
                :options="options.country"
                :reduce="option => option.name"
                class="w-full md:w-[90%] text-bw-400"
              >
              </v-select>
              <div
                v-for="error in v$.country.$errors"
                :key="error.$uid"
                class="mt-2"
              >
                <div class="text-red-600 flex items-center gap-2 text-sm">
                  <NuxtIcon name="cross" /> {{ error.$message }}
                </div>
              </div>
            </div>
            <div class="mb-6">
              <label class="block mr-4" for="message"
                >Message<span class="text-[#EB3133]">*</span></label
              >
              <textarea
                id="message"
                v-model.trim="formData.message"
                required
                name="message"
                rows="10"
                class="form-input-text-area px-4 py-2 mt-3 w-full md:w-[90%]"
                :class="{ 'error-input-text-area': v$.message.$error }"
                autocomplete="off"
                placeholder="Type about your project"
                @blur="v$.message.$touch()"
              ></textarea>
              <div class="flex justify-end gap-2 w-full md:w-[90%]">
                <div
                  v-for="error in v$.message.$errors"
                  :key="error.$uid"
                  class="mt-2 text-area-error"
                >
                  <div class="text-red-600 flex items-center gap-2 text-sm">
                    <NuxtIcon name="cross" /> {{ error.$message }}
                  </div>
                </div>
                <p class="text-xs text-right mt-3 mb-0 mr-0">
                  {{ formData.message?.length }}/{{
                    rules.message.maxLength.$params.max
                  }}
                </p>
              </div>
            </div>
            <button
              class="disabled:bg-[#B9BABD] disabled:text-[#F2F2F2] disabled:border-0 bg-white border border-primary-900 text-primary-primary hover:bg-primary-primary hover:text-white px-4 py-3 rounded disabled:opacity-40 text-base font-medium flex"
              type="submit"
              :disabled="v$.$invalid || isSubmitting"
            >
              <span v-if="isSubmitting" class="spinner w-6 h-6"></span> Send
              Request
            </button>
            <template v-if="isError">
              <div
                v-for="(err, index) in errorResult.error.details.errors"
                :key="index"
                class="text-red-600 text-sm mt-4"
              >
                {{ err.message }}
              </div>
            </template>
          </form>
          <div v-else class="w-[90%] text-bw-900 mt-8">
            <NuxtIcon
              name="check-circle"
              filled
              class="check-circle-icon w-fit block p-3 md:p-4 bg-[#A2D7C3] rounded-full bg-opacity-40"
            />
            <div class="submit-message mt-6">
              <p class="text-base md:text-lg font-medium mb-6">
                We're just connected, thank you!
              </p>
              <p class="text-sm md:text-base text-bw-800 mb-10">
                We are glad to get in touch with you. If you have a question and
                suggestion regarding to our services, we are ready to help you.
                <span
                  class="text-primary-primary cursor-pointer hover:underline"
                  @click="askMoreQuestions"
                  >Ask another question</span
                >
              </p>
              <p class="text-sm md:text-base text-bw-800 mb-4">Best regards,</p>
              <p class="text-sm md:text-base text-bw-800">Suitmedia</p>
            </div>
          </div>
        </Transition>
      </div>

      <div v-if="isLoading" class="w-full md:w-1/2 text-bw-900 mt-14 md:mt-0">
        <h2 class="section-heading">Find Our Creative Hub</h2>
        <div class="office flex flex-col gap-8 mt-8 md:mt-10">
          <div class="head-office flex flex-col gap-2">
            <UtilsSkeletonLoading class="w-1/3 h-10" />
            <UtilsSkeletonLoading class="w-1/4 h-8" />
            <UtilsSkeletonLoading class="w-2/3 h-15" />
            <UtilsSkeletonLoading class="w-1/2 h-6" />
          </div>
          <div class="branch-office">
            <UtilsSkeletonLoading class="w-1/3 h-10 mb-4" />
            <div v-for="i in 4" :key="i" class="mb-6 flex flex-col gap-2">
              <UtilsSkeletonLoading class="w-1/4 h-8" />
              <UtilsSkeletonLoading class="w-2/3 h-15" />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="w-full md:w-1/2 mt-14 md:mt-0">
        <h2
          class="text-[22px] md:text-[32px] leading-normal font-medium text-bw-900"
        >
          Find Our Creative Hub
        </h2>
        <div class="office flex flex-col gap-8 mt-8 md:mt-10">
          <div class="head-office">
            <h3
              class="font-medium leading-normal text-lg md:text-[26px] mb-4 text-bw-900"
            >
              Head Office
            </h3>
            <h4
              class="leading-normal text-base md:text-[22px] mb-3 text-bw-900"
            >
              {{ headOffice.attributes?.location }}
            </h4>
            <NuxtLink
              :to="headOffice.attributes?.gmap_url"
              :title="headOffice.attributes?.address"
              class="text-sm md:text-base mb-4 text-bw-800 w-full md:w-[70%] block hover:text-primary-primary"
              target="_blank"
              rel="noopener"
            >
              {{ headOffice.attributes?.address }}
            </NuxtLink>
            <NuxtLink
              v-if="headOffice.attributes?.is_head_office"
              :href="`mailto:${headOffice.attributes?.email}`"
              :title="headOffice.attributes?.email"
              class="px-3 py-2 bg-orange-100 rounded-md flex items-center gap-1 md:w-max hover:bg-orange-500 hover:text-white mail-to w-fit text-sm md:text-base text-bw-900"
              ><NuxtIcon name="sm-mail" filled class="sm-mail-icon" />
              {{ headOffice.attributes?.email }}</NuxtLink
            >
          </div>
          <div class="branch-office">
            <h3
              class="font-medium leading-normal text-lg md:text-[26px] mb-4 text-bw-900"
            >
              Branch Office
            </h3>
            <div v-for="office in branchOffices" :key="office.id" class="mb-6">
              <h4
                class="leading-normal text-base md:text-[22px] mb-2 text-bw-900"
              >
                {{ office.attributes?.location }}
              </h4>
              <NuxtLink
                :to="office.attributes?.gmap_url"
                :title="office.attributes?.address"
                class="text-sm md:text-base text-bw-800 w-full md:w-2/3 block hover:text-primary-primary"
                target="_blank"
                rel="noopener"
              >
                {{ office.attributes?.address }}
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { useVuelidate } from '@vuelidate/core'
import {
  required,
  email,
  minLength,
  maxLength,
  helpers
} from '@vuelidate/validators'
import { useGlobalStore } from '~/store/global'

const global = useGlobalStore()
const mainSeo = global.mainSeo

const route = useRoute()
const SEOMeta = ref({})
const { executeRecaptcha } = useRecaptcha()

const isLoading = ref(true)
const isSubmitting = ref(false)
const isSubmitted = ref(false)
const isError = ref(false)
const errorResult = ref({})

const banner = ref({})
const officesData = ref([])
const headOffice = ref([])
const branchOffices = ref([])

const formWrapper = ref(null)

const formData = ref({
  subject: '',
  name: '',
  company: '',
  email: '',
  phone: null,
  country: '',
  message: ''
})

const workingEmail = value =>
  !value.includes('@gmail') &&
  !value.includes('@aol') &&
  !value.includes('@hotmail') &&
  !value.includes('@yahoo') &&
  !value.includes('@outlook')

const numPlusValidator = helpers.regex(/\+?\d+/)

const rules = {
  subject: { required, maxLength: maxLength(256) },
  name: { required, minLength: minLength(4), maxLength: maxLength(256) },
  company: { required, minLength: minLength(3), maxLength: maxLength(256) },
  email: {
    required,
    email,
    minLength: minLength(4),
    maxLength: maxLength(256),
    workingEmail: helpers.withMessage('Please use work email', workingEmail)
  },
  phone: {
    required,
    minLength: minLength(7),
    maxLength: maxLength(256),
    numPlusValidator
  },
  country: { required, maxLength: maxLength(256) },
  message: { required, minLength: minLength(7), maxLength: maxLength(2048) }
}

const v$ = useVuelidate(rules, formData)

const options = ref({
  subject: [],
  country: []
})

const [pageBanner, offices, countries, seo, subjects] = await Promise.all([
  useSuitmediaApiData(`/api/banners`, {
    query: {
      'filters[url]': route.path,
      'fields[0]': 'id',
      'populate[banner][fields][0]': 'title',
      'populate[banner][fields][1]': 'description',
      'populate[banner][fields][2]': 'cta_wording',
      'populate[banner][fields][3]': 'cta_url',
      'populate[banner][populate][4]': 'image',
      'populate[banner][populate][5]': 'background_image'
    }
  }),
  useSuitmediaApiData(`/api/offices`, {
    query: {
      'fields[0]': 'id',
      'fields[1]': 'location',
      'fields[2]': 'address',
      'fields[3]': 'email',
      'fields[4]': 'gmap_url',
      'fields[5]': 'is_head_office'
    }
  }),
  useSuitmediaApiData(`/api/countries`, {
    query: {
      'fields[0]': 'id',
      'fields[1]': 'country'
    }
  }),
  useSuitmediaApiData(`/api/seo-metas`, {
    query: {
      'filters[url]': route.path,
      populate: 'seo.image'
    }
  }),
  useSuitmediaApiData(`/api/contact-subjects`, {
    query: {
      'field[0]': 'subject'
    }
  })
])

isLoading.value =
  offices.pending.value || countries.pending.value || subjects.pending.value

if (offices.error.value || countries.error.value || subjects.error.value) {
  throw createError({
    statusCode: 404,
    message: `${offices.error.value ? 'Offices' : 'Countries'} does not exist`
  })
}

SEOMeta.value = seo.data.value.data[0]
banner.value = pageBanner.data.value.data[0]
options.value.country = countries.data.value.data.map(item => {
  return {
    id: item.id,
    name: item.attributes.country
  }
})
officesData.value = offices.data.value.data
headOffice.value = offices.data.value.data.find(
  item => item.attributes?.is_head_office
)
branchOffices.value = offices.data.value.data.filter(
  item => !item.attributes?.is_head_office
)
options.value.subject = subjects.data.value.data.map(item => {
  return {
    name:
      item?.attributes?.subject === 'New Business'
        ? 'Start New Project'
        : item.attributes.subject,
    ...item.attributes
  }
})

onMounted(() => {
  formData.value.subject = options?.value?.subject?.find(
    sub => sub.subject === 'Start New Project'
  )?.subject

  formData.value.country = options?.value?.country?.find(
    item => item.name === 'Indonesia'
  )?.name
})

const bgImage = computed(() => {
  return banner.value?.attributes?.banner?.background_image?.data?.attributes
    ?.url
})

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  url: SEOMeta?.value?.attributes?.url || 'suitmedia.com',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  image:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    banner.value?.attributes?.banner?.background_image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png'
})

useSeoMeta({
  title: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  ogTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    banner.value?.attributes?.banner?.background_image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png',
  ogType: SEOMeta?.value?.attributes?.seo?.open_graph_type,
  twitterTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  twitterDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  twitterImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    banner.value?.attributes?.banner?.background_image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png'
})

const submitForm = async _values => {
  isSubmitting.value = true
  const { token } = await executeRecaptcha('submit')

  const payload = {
    subject: formData.value.subject,
    name: formData.value.name,
    company: formData.value.company,
    email: formData.value.email,
    phone_number: formData.value.phone,
    country: formData.value.country,
    message: formData.value.message,
    g_recaptcha_response: token
  }

  const { pending, error } = await useSuitmediaApiData(`/api/contacts`, {
    method: 'post',
    cache: false,
    body: {
      data: payload
    }
  })

  if (error.value) {
    isSubmitting.value = pending.value
    isError.value = true
    errorResult.value = error.value.data
    throw createError({
      ...error.value,
      statusCode: error.value.statusCode,
      statusMessage: error.value.statusMessage
    })
  } else {
    isSubmitting.value = pending.value
    isSubmitted.value = true
  }

  formWrapper.value.scrollIntoView({ block: 'start', behavior: 'smooth' })
}

const contactForm = ref(null)
const askMoreQuestions = () => {
  isSubmitted.value = false
  isError.value = false
  formData.value.subject =
    options?.value?.subject?.find(sub => sub.subject === 'Start New Project')
      ?.subject || ''
  formData.value.name = ''
  formData.value.company = ''
  formData.value.email = ''
  formData.value.phone = null
  formData.value.country = formData.value.country =
    options?.value?.country?.find(item => item.name === 'Indonesia')?.name || ''
  formData.value.message = ''
  v$.value.$reset()
}
</script>

<style lang="scss" scoped>
.form-input {
  width: 100%;

  @media screen and (min-width: 768px) {
    width: 90%;
  }
}

.form {
  :deep(.v-select) {
    ul {
      color: #353535;
    }
  }

  :deep(.vs__selected) {
    color: #4b4b4b;
  }

  :deep(.vs--open) {
    .vs__dropdown-toggle {
      padding: 10px;

      background: linear-gradient(#ff6700, #ff6700),
        linear-gradient(#ff6700, #ff6700), linear-gradient(#ff6700, #ff6700);
      background-size:
        1px 20%,
        100% 1px,
        1px 20%;
      background-position:
        bottom left,
        bottom center,
        bottom right;
      background-repeat: no-repeat;

      border: none;
    }
  }

  :deep(.vs__dropdown-toggle) {
    padding: 10px;

    background: linear-gradient(#a5a5a5, #a5a5a5),
      linear-gradient(#a5a5a5, #a5a5a5), linear-gradient(#a5a5a5, #a5a5a5);
    background-size:
      1px 20%,
      100% 1px,
      1px 20%;
    background-position:
      bottom left,
      bottom center,
      bottom right;
    background-repeat: no-repeat;

    border: none;
    border-radius: 0;
    // color: #999;
  }
}

:deep(.mail-to) {
  word-break: break-word;
  border: 1px solid #ff6700;

  .sm-mail-icon {
    svg {
      width: 24px;
      height: 24px;

      path {
        fill: #353535 !important;
      }
    }
  }

  &:hover {
    .sm-mail-icon {
      svg {
        path {
          fill: #fff !important;
        }
      }
    }
  }
}

:deep(.check-circle-icon) {
  svg {
    width: 20px;
    height: 20px;
  }

  @media screen and (min-width: 768px) {
    svg {
      width: 30px;
      height: 30px;
    }
  }
}

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

input[type='number'] {
  -moz-appearance: textfield;
}

.text-area-error {
  width: -moz-available;
  width: -webkit-fill-available;
}
.spinner {
  display: block;
  // width: 42px;
  // height: 42px;
  margin-right: 10px;
  border-radius: 50%;
  border: 5px solid rgba(#000, 0.1);
  border-top-color: #ff6700;
  animation: spinning 1s infinite both;
}

@keyframes spinning {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.contact-form-enter-active {
  transition: all 0.2s ease-in-out;
}

.contact-form-enter-from {
  opacity: 0;
  transform: translateX(-50px);
}
</style>
